import Button from '../../../ui_components/button/button';
import Checkbox from '../../../ui_components/checkbox/checkbox';
import ValidationForm, {
  FormValidationError,
} from '../../../ui_components/validation/validationForm';
import { PopoverScrollContext } from '../../../ui_components/popover/popover';
import { Modal, ModalActions, ModalHeader } from '../../../ui_components/modal';
import PageDimmer from '../../../ui_components/dimmer/PageDimmer';
import { CategoryIds, ItemClasses, ItemTypes } from '../types';
import {
  FabricsOrLiningsItemDetailsFieldsPartial,
  fabricsOrLiningsItemDetailsFieldsInitialState,
  fabricsOrLiningsItemDetailsFieldsReducer,
  FabricsOrLiningsItemDetailsFields,
} from '../../../redux/modules/my-shop/fabrics-or-linings-item-details-fields-reducer';
import {
  FormFieldsPartial,
  formFieldsInitialState,
  formFieldsReducer,
} from '../../../redux/modules/my-shop/form-fields-reducer';
import { ItemModalContent, ItemTabs, TabContainer } from '../styles';
import React, {
  FC,
  FormEvent,
  KeyboardEvent,
  useEffect,
  useReducer,
  useRef,
  useState,
} from 'react';
import { Tab, TabList } from 'react-tabs';
import { toast } from 'sonner';
import {
  addFabricsOrLiningsRecord,
  addRecord,
  getFabricsOrLiningsRecord,
  getRecord,
  updateFabricsOrLiningsRecord,
  updateRecord,
} from '../../../api/item';
import { getAccount, uploadRemoteImage } from '../../../api/account';
import styled from '../../../theme';

import { Category } from '../../../interfaces/category';
import Details from './details';
import Pricing from './pricing';
import Specifications from './specifications';
import { Supplier } from '../../../interfaces/supplier';
import { detect } from 'detect-browser';
import { getSelectedName } from '../helpers';
import { DraperyOrderFabric } from '../../calculator/calculator';
import { Client } from '../../../redux/modules/client';
import _ from 'lodash';
import { confirmation } from '../../../confirmation';
import { Prompt } from 'react-router-dom';
import { trackView } from '../../analytics/track_view';
import { MixpanelEvents } from '../../analytics/mix_panel_events';
import WideSheet from '../../wide_sheet/WideSheet';
import MyShopItemWideSheetContent, {
  MyShopSheetValidationState,
} from './my-shop-item-wide-sheet-content';
import { isAbsoluteImageSrc } from '../../../utils/image-src';

type AccountDescriptionSettings = {
  orderingDescription?: any[] | null;
  salesDescriptionCom?: any[] | null;
  salesDescriptionWorkroom?: any[] | null;
};

const CancelButton = styled(Button)``;

const ModalItem = styled(Modal)`
  width: 90% !important;
  min-height: 300px !important;
  bottom: 0px !important;
`;

const ValidationFormItem = styled(ValidationForm)`
  min-height: inherit !important;
`;

const WIDE_SHEET_CLOSE_ANIMATION_MS = 300;

const getScopedAccountId = (): number => {
  if (typeof window === 'undefined') {
    return 0;
  }

  return Number(
    localStorage.getItem('ownerAccountId') ||
      localStorage.getItem('accountId') ||
      '0'
  );
};

const uploadRemoteAvatarIfNeeded = async (avatarId?: string | null): Promise<string | null> => {
  if (!avatarId || !isAbsoluteImageSrc(avatarId) || !/^https?:\/\//i.test(avatarId)) {
    return avatarId || null;
  }

  try {
    const result = await uploadRemoteImage(avatarId, getScopedAccountId());
    if (result?.error) {
      throw new Error(result.message || 'Unable to upload remote image.');
    }
    return result?.url || avatarId;
  } catch (error) {
    console.error('Unable to import remote My Shop item image:', error);
    return avatarId;
  }
};

interface StandardItemModalProps {
  isModalOpen: boolean;
  categories: Category[];
  suppliers: Supplier[];
  itemId: number | null;
  activeItemClassId: number | null;
  tabIndex?: number;
  onModalClose(item?: any): void;
  reloadGrid?(): void;
  openModalClick?(): void;
  highlightedSection?: string | null;
  wideSheetFocusField?: string | null;
  itemName?: string | null;
  validateAll?(mode: string): boolean;
  mainFabric?: DraperyOrderFabric;
  indexOfMainFabric?: number;
  handleCloseModal?(): void;
  resetSupplier?(): void;
  categoryId?: number;
  updateStateByName?(
    name: string,
    value: string | number | boolean | number[] | undefined | object,
    isSaving?: boolean
  ): void;
  pathIdForModal?: string | null;
  pathNameForModal?: string;
  fabricPatternModalCategoryId?: number;

  clearHighlightSection?(name: string): void;
  highLightSection?(name: string): void;
  client?: Client;
  supplierName?: string;
  setSupplierName?: (name: string) => void;
  supplierId?: number | null;
  updateFabricsTab?(payload: any): void;
  fromCalculator?: boolean;
  draperyOrderFabricsErrors?: any[];
  useWideSheet?: boolean;
  readOnlyPricing?: boolean;
  hideSubtype?: boolean;
  isSupplierModalOpen?: boolean;
  canEditMyShop?: boolean;
  prefill?: any;
  detailsPrefill?: FabricsOrLiningsItemDetailsFieldsPartial | null;
  lockPrefill?: boolean;
}

interface ScrollInfo {
  scrollX: number;
  scrollY: number;
}

const getMappedItemTypeId = (
  categoryId: number,
  categories: Category[],
  fallbackCategoryName?: string,
  parentCategoryId?: number
): number => {
  const selectedCategory = categories.find((category) => category.id === categoryId);
  const rootCategory = parentCategoryId
    ? categories.find((category) => category.id === parentCategoryId)
    : selectedCategory?.parentCategoryId
      ? categories.find((category) => category.id === selectedCategory.parentCategoryId)
      : selectedCategory;
  const rootCategoryName = rootCategory?.name || fallbackCategoryName || '';

  return rootCategoryName === 'Fees' ? ItemTypes.Product : ItemTypes.Service;
};

const normalizeCategoryFields = (
  itemUpdates: FormFieldsPartial,
  categories: Category[]
): Pick<
  FormFieldsPartial,
  'categoryId' | 'subcategoryId' | 'parentCategoryId' | 'categoryName'
> => {
  const explicitSubcategory = itemUpdates.subcategoryId
    ? categories.find((cat) => cat.id === itemUpdates.subcategoryId)
    : undefined;
  const selectedCategory = itemUpdates.categoryId
    ? categories.find((cat) => cat.id === itemUpdates.categoryId)
    : undefined;

  if (explicitSubcategory && itemUpdates.categoryId) {
    const rootCategory =
      categories.find((cat) => cat.id === itemUpdates.categoryId) || selectedCategory;

    return {
      categoryId: itemUpdates.categoryId,
      subcategoryId: explicitSubcategory.id,
      parentCategoryId: rootCategory?.id,
      categoryName: rootCategory?.name || '',
    };
  }

  if (selectedCategory?.parentCategoryId) {
    const rootCategory = categories.find((cat) => cat.id === selectedCategory.parentCategoryId);

    return {
      categoryId: rootCategory?.id || selectedCategory.id,
      subcategoryId: selectedCategory.id,
      parentCategoryId: rootCategory?.id,
      categoryName: rootCategory?.name || selectedCategory.name,
    };
  }

  return {
    categoryId: itemUpdates.categoryId || 0,
    subcategoryId: null,
    parentCategoryId: undefined,
    categoryName: selectedCategory?.name || '',
  };
};

const getDefaultCategoryFields = (
  categoryId: number,
  categories: Category[]
): Pick<
  FormFieldsPartial,
  'categoryId' | 'subcategoryId' | 'parentCategoryId' | 'categoryName' | 'itemTypeId'
> => {
  const selectedCategory = categories.find((category) => category.id === categoryId);
  const categoryName =
    selectedCategory?.name ||
    (categoryId === CategoryIds.FabricsAndTrims
      ? 'Fabrics & Trims'
      : categoryId === CategoryIds.Linings
        ? 'Linings'
        : '');

  return {
    categoryId,
    subcategoryId: null,
    parentCategoryId: undefined,
    categoryName,
    itemTypeId: getMappedItemTypeId(categoryId, categories, categoryName),
  };
};

const formatFetchedSpecificationValue = (
  value: string | number | null | undefined,
  fallback?: string
): string | undefined => {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }

  const normalizedValue = String(value).trim();

  if (!normalizedValue) {
    return fallback;
  }

  return Number(normalizedValue) === 0 ? '0.00' : normalizedValue;
};

const StandardItemModal: FC<StandardItemModalProps> = ({
  tabIndex,
  isModalOpen,
  categories,
  suppliers,
  itemId,
  activeItemClassId,
  onModalClose,
  reloadGrid,
  openModalClick,
  highlightedSection,
  wideSheetFocusField,
  validateAll,
  mainFabric,
  updateStateByName,
  indexOfMainFabric,
  categoryId,
  pathIdForModal,
  pathNameForModal,
  fabricPatternModalCategoryId,
  handleCloseModal,
  clearHighlightSection,
  highLightSection,
  client,
  supplierName,
  setSupplierName,
  supplierId,
  resetSupplier,
  updateFabricsTab,
  fromCalculator,
  draperyOrderFabricsErrors,
  useWideSheet = false,
  readOnlyPricing = false,
  hideSubtype = false,
  isSupplierModalOpen = false,
  canEditMyShop = true,
  prefill = null,
  detailsPrefill = null,
  lockPrefill = false,
}) => {
  const browser = detect();
  const inactiveItemError = draperyOrderFabricsErrors
    ? draperyOrderFabricsErrors.some((error) => error.name === pathNameForModal)
    : false;

  const [isCloseModalActive, setIsCloseModalActive] = useState<boolean>(false);
  const [isWideSheetVisible, setIsWideSheetVisible] = useState<boolean>(false);
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);
  const [isCategory, setIsCategory] = useState(false);
  const [isCategoryErrorMessage, setIsCategoryErrorMessage] = useState(false);
  const [isName, setIsName] = useState(false);
  const [isNameMessageError, setIsNameMessageError] = useState<boolean>(false);
  const [areaScrollInfo, setAreaScrollInfo] = useState<ScrollInfo>({
    scrollX: 0,
    scrollY: 0,
  });
  const [formFields, dispatch] = useReducer(formFieldsReducer, formFieldsInitialState);
  const [copyFormFields, copyDispatch] = useReducer(formFieldsReducer, formFieldsInitialState);
  const [formValues, setFormValues] = useState<FormFieldsPartial>(formFields);
  const [isUpdateChanged, setIsUpdateChanged] = useState<boolean>(false);
  const [fabricsOrLiningsItemDetailsFields, dispatchItemDetails] = useReducer(
    fabricsOrLiningsItemDetailsFieldsReducer,
    fabricsOrLiningsItemDetailsFieldsInitialState
  );
  let isFabricsOrLiningsCategory: boolean = false;
  const [errorElementId, setErrorElementId] = useState<string | null>(null);
  const [categoryNameNew, setCategoryName] = useState('');
  const [validationError, setValidationError] = useState<boolean>(false);
  const initialSheetValidationErrors: MyShopSheetValidationState = {
    category: false,
    supplier: false,
    name: false,
  };
  const [sheetValidationErrors, setSheetValidationErrors] = useState<MyShopSheetValidationState>(
    initialSheetValidationErrors
  );
  const [submitErrorMessage, setSubmitErrorMessage] = useState<string>('');
  const [isSavingWideSheet, setIsSavingWideSheet] = useState(false);
  const [refetchItemTrigger, setRefetchItemTrigger] = useState(false);
  const [descriptionSettings, setDescriptionSettings] = useState<AccountDescriptionSettings>({
    orderingDescription: [],
    salesDescriptionCom: [],
    salesDescriptionWorkroom: [],
  });
  const hasAppliedDefaultFabricPatternCategory = useRef(false);
  const wideSheetCloseTimeoutRef = useRef<number | null>(null);

  if (formFields.parentCategoryId || formFields.categoryId) {
    isFabricsOrLiningsCategory = [CategoryIds.FabricsAndTrims, CategoryIds.Linings].includes(
      formFields.parentCategoryId || formFields.categoryId
    );
  }

  const handlePrefillItem = () => {
    const hash_to_upload: FormFieldsPartial = {};

    Object.entries(prefill).forEach(([key, value]) => {
      if (key in formFields) {
        hash_to_upload[key] = value;
      }
    });

    dispatch({
      type: 'updateFormFields',
      formFieldUpdates: hash_to_upload,
    });
  };

  const handlePrefillDetails = () => {
    const detailsUpdates: FabricsOrLiningsItemDetailsFieldsPartial = {};

    Object.entries(detailsPrefill || {}).forEach(([key, value]) => {
      if (key in fabricsOrLiningsItemDetailsFields) {
        detailsUpdates[key as keyof FabricsOrLiningsItemDetailsFields] = value as any;
      }
    });

    dispatchItemDetails({
      type: 'updateFormFields',
      formFieldUpdates: detailsUpdates,
    });
  };

  useEffect(() => {
    if (prefill) {
      handlePrefillItem();
    }

    if (detailsPrefill) {
      handlePrefillDetails();
    }

    return () => {};
  }, [prefill, detailsPrefill]);

  useEffect(() => {
    if (tabIndex !== undefined) {
      setActiveTabIndex(tabIndex);
    }
  }, [tabIndex]);

  useEffect(() => {
    if (!isModalOpen) {
      return;
    }

    let isSubscribed = true;

    const loadDescriptionSettings = async () => {
      const scopedAccountId = Number(
        localStorage.getItem('ownerAccountId') || localStorage.getItem('accountId') || '0'
      );

      if (!scopedAccountId) {
        return;
      }

      try {
        const account: any = await getAccount(scopedAccountId);

        if (!isSubscribed || !account || account.error) {
          return;
        }

        setDescriptionSettings({
          orderingDescription: account.orderingDescription || [],
          salesDescriptionCom: account.salesDescriptionCom || [],
          salesDescriptionWorkroom: account.salesDescriptionWorkroom || [],
        });
      } catch (error) {
        console.error('Failed to load account description settings', error);
      }
    };

    loadDescriptionSettings();

    return () => {
      isSubscribed = false;
    };
  }, [isModalOpen]);

  const previousIsSupplierModalOpen = useRef(isSupplierModalOpen);

  useEffect(() => {
    const wasSupplierModalOpen = previousIsSupplierModalOpen.current;
    previousIsSupplierModalOpen.current = isSupplierModalOpen;

    if (!useWideSheet || isSupplierModalOpen || !wasSupplierModalOpen) {
      return;
    }

    if (!supplierName || supplierId === null || supplierId === undefined || supplierId <= 0) {
      return;
    }

    if (formFields.supplierId === supplierId && formFields.supplierName === supplierName) {
      return;
    }

    dispatch({
      type: 'updateFormFields',
      formFieldUpdates: {
        supplierId,
        supplierName,
      },
    });
  }, [
    formFields.supplierId,
    formFields.supplierName,
    isSupplierModalOpen,
    supplierId,
    supplierName,
    useWideSheet,
  ]);

  useEffect(() => {
    if (!useWideSheet) {
      return;
    }

    if (wideSheetCloseTimeoutRef.current !== null) {
      window.clearTimeout(wideSheetCloseTimeoutRef.current);
      wideSheetCloseTimeoutRef.current = null;
    }

    if (!isModalOpen) {
      setIsWideSheetVisible(false);
      return;
    }

    setIsWideSheetVisible(false);
    const frameId = window.requestAnimationFrame(() => {
      setIsWideSheetVisible(true);
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [isModalOpen, useWideSheet]);

  useEffect(() => {
    if (!useWideSheet || !isModalOpen || !isWideSheetVisible || !wideSheetFocusField) {
      return;
    }

    let isCancelled = false;
    let retryTimeoutId: number | null = null;
    const selector = `[data-sheet-focus-field="${wideSheetFocusField}"]`;

    const focusField = (attempt = 0) => {
      if (isCancelled) {
        return;
      }

      const field = document.querySelector(selector) as HTMLElement | null;

      if (!field) {
        if (attempt < 12) {
          retryTimeoutId = window.setTimeout(() => {
            focusField(attempt + 1);
          }, 75);
        }
        return;
      }

      field.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if ('focus' in field) {
        field.focus();
      }

      field.classList.remove('sheet-error-hover');
      void field.offsetWidth;
      field.classList.add('sheet-error-hover');

      retryTimeoutId = window.setTimeout(() => {
        field.classList.remove('sheet-error-hover');
      }, 1200);
    };

    retryTimeoutId = window.setTimeout(() => {
      focusField();
    }, 50);

    return () => {
      isCancelled = true;

      if (retryTimeoutId !== null) {
        window.clearTimeout(retryTimeoutId);
      }
    };
  }, [isModalOpen, isWideSheetVisible, useWideSheet, wideSheetFocusField]);

  useEffect(() => {
    return () => {
      if (wideSheetCloseTimeoutRef.current !== null) {
        window.clearTimeout(wideSheetCloseTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setFormValues((prevState) => ({
      ...prevState,
      salesDescription: formFields.salesDescription,
      salesDescriptionWorkroom: formFields.salesDescriptionWorkroom,
      orderingDescription: formFields.orderingDescription,
    }));
  }, [
    formFields.name,
    formFields.fabricsOrLiningsItemDetails &&
      formFields.fabricsOrLiningsItemDetails.fabricWidthInches,
    formFields.fabricsOrLiningsItemDetails &&
      formFields.fabricsOrLiningsItemDetails.verticalRepeatInches,
    formFields.fabricsOrLiningsItemDetails &&
      formFields.fabricsOrLiningsItemDetails.horizontalRepeatInches,
    formFields.fabricsOrLiningsItemDetails && formFields.fabricsOrLiningsItemDetails.isDropRepeatOn,
    formFields.fabricsOrLiningsItemDetails &&
      formFields.fabricsOrLiningsItemDetails.isSpecialtyFabricHandlingOn,
  ]);

  useEffect(() => {
    if (formValues.id) {
      let hasChanged = false;
      for (let key1 in formValues) {
        for (let key2 in formFields) {
          if (key1 === key2 && !_.isEqual(formValues[key1], formFields[key1])) {
            hasChanged = true;
            break;
          }
        }
      }
      setIsUpdateChanged(hasChanged);
    }
  }, [formValues, formFields]);

  if (mainFabric && mainFabric.values) {
    fabricsOrLiningsItemDetailsFieldsInitialState.fabricWidthInches = String(
      mainFabric.values.fabricWidth
    );
    fabricsOrLiningsItemDetailsFieldsInitialState.horizontalRepeatInches = String(
      mainFabric.values.horizontalRepeat
    );
    fabricsOrLiningsItemDetailsFieldsInitialState.verticalRepeatInches = String(
      mainFabric.values.verticalRepeat
    );
  }

  const handleInvalidElem = (elem: HTMLElement): void => {
    const elemId = elem.id;

    if (['name', 'itemCategory', 'itemTypeProduct', 'itemTypeService'].includes(elemId)) {
      setValidationError((prev) => !prev);
      setIsNameMessageError(true);
      setActiveTabIndex(0);
      if (!formFields.categoryId) {
        setIsCategoryErrorMessage(true);
        setIsCategory(true);
      }
    } else {
      setIsNameMessageError(false);
      setActiveTabIndex(1);
    }

    setErrorElementId(elemId);
  };

  const highlightFirstInvalidWideSheetField = (errors: MyShopSheetValidationState): void => {
    const firstInvalidFieldId = errors.category
      ? 'itemCategory'
      : errors.supplier
        ? 'itemSupplier'
        : errors.name
          ? 'my-shop-item-name'
          : '';

    if (!firstInvalidFieldId || typeof window === 'undefined') {
      return;
    }

    window.requestAnimationFrame(() => {
      const field = document.getElementById(firstInvalidFieldId) as HTMLElement | null;
      if (!field) {
        return;
      }

      field.scrollIntoView({ behavior: 'smooth', block: 'center' });

      field.classList.remove('sheet-error-hover');
      void field.offsetWidth;
      field.classList.add('sheet-error-hover');

      window.setTimeout(() => {
        field.classList.remove('sheet-error-hover');
      }, 1200);
    });
  };

  const submitForm = async (): Promise<void> => {
    if (useWideSheet) {
      setIsSavingWideSheet(true);
    }

    try {
      setSubmitErrorMessage('');
      const {
        name,
        itemNumber,
        avatarId,
        categoryId,
        subcategoryId,
        customerFacingName,
        url,
        notes,
        isActive,
        cost,
        msrp,
        retailPricingMethod,
        retailPricingValue,
        tradePricingMethod,
        tradePricingValue,
        shippingCost,
        sihCostType,
        salesDescriptionWorkroom,
        handlingFeeMethod,
        handlingFeeValue,
      } = formFields;

      const normalizedSupplierId = useWideSheet
        ? formFields.supplierId === null ||
          formFields.supplierId === undefined ||
          formFields.supplierId <= 0
          ? null
          : formFields.supplierId
        : formFields.supplierId === null || formFields.supplierId === undefined
          ? 0
          : formFields.supplierId;

      let selectedCategory = categories?.find((f: any) => f.id === categoryId);
      let noSupplierRequired =
        categoryId === 102 || selectedCategory?.name?.toLowerCase() === 'fees';

      if (useWideSheet) {
        let nextSheetValidationErrors: MyShopSheetValidationState = {
          category: !categoryId,
          supplier: noSupplierRequired ? false : !normalizedSupplierId,
          name: !name,
        };

        setSheetValidationErrors(nextSheetValidationErrors);

        if (
          nextSheetValidationErrors.category ||
          nextSheetValidationErrors.supplier ||
          nextSheetValidationErrors.name
        ) {
          highlightFirstInvalidWideSheetField(nextSheetValidationErrors);
          return;
        }
      } else {
        if (!categoryId) {
          setActiveTabIndex(0);
          setIsCategory(true);
          setIsCategoryErrorMessage(true);
          return;
        }
        if (!name) {
          setActiveTabIndex(0);
          setIsNameMessageError(true);
          setIsName(true);
          return;
        }
      }

      const salesTextArea = document.getElementById('salesDescription') as HTMLInputElement;

      const orderingTextArea = document.getElementById('orderingDescription') as HTMLInputElement;

      const mappedItemTypeId = getMappedItemTypeId(
        categoryId,
        categories,
        formFields.categoryName,
        formFields.parentCategoryId
      );

      let normalizedAvatarId = avatarId;
      if (useWideSheet) {
        normalizedAvatarId = await uploadRemoteAvatarIfNeeded(avatarId);
        if (normalizedAvatarId !== avatarId) {
          dispatch({
            type: 'updateFormFields',
            formFieldUpdates: { avatarId: normalizedAvatarId },
          });
        }
      }

      let payload: FormFieldsPartial = {
        name,
        itemNumber,
        avatarId: normalizedAvatarId,
        itemTypeId: mappedItemTypeId,
        supplierId: normalizedSupplierId,
        categoryId,
        subcategoryId: subcategoryId || null,
        customerFacingName: customerFacingName || null,
        url: url || null,
        notes,
        isActive,
        cost,
        msrp,
        retailPricingMethod,
        retailPricingValue,
        tradePricingMethod,
        tradePricingValue,
        shippingCost,
        sihCostType,
        salesDescriptionWorkroom,
        handlingFeeMethod,
        handlingFeeValue,
      };

      for (let key in payload) {
        if (
          isFabricsOrLiningsCategory &&
          updateStateByName &&
          [
            'cost',
            'msrp',
            'retailPricingMethod',
            'retailPricingValue',
            'tradePricingMethod',
            'tradePricingValue',
            'sihCostType',
            'handlingFeeMethod',
            'handlingFeeValue',
            'shippingCost',
          ].includes(key) &&
          indexOfMainFabric !== undefined &&
          indexOfMainFabric >= 0
        ) {
          if (formFields.categoryId === 101) {
            updateStateByName(`fabricsPatternItems.${indexOfMainFabric}.${key}`, payload[key]);
          } else if (formFields.categoryId === 110) {
            updateStateByName(`liningsPatternItems.${indexOfMainFabric}.${key}`, payload[key]);
          }
        }
      }
      payload.salesDescription = salesTextArea ? salesTextArea.value : formFields.salesDescription;

      payload.orderingDescription = orderingTextArea
        ? orderingTextArea.value
        : formFields.orderingDescription;

      payload.salesDescriptionWorkroom = formFields.salesDescriptionWorkroom;

      if (useWideSheet) {
        const wideSheetPayload = payload as any;

        wideSheetPayload.sihCostType = sihCostType || null;
        wideSheetPayload.handlingFeeMethod = null;
        wideSheetPayload.handlingFeeValue = null;

        if (!sihCostType || !shippingCost) {
          wideSheetPayload.shippingCost = null;
          wideSheetPayload.sihCostType = null;
        }
      }

      if (isFabricsOrLiningsCategory) {
        const {
          fabricWidthInches,
          verticalRepeatInches,
          horizontalRepeatInches,
          isDropRepeatOn,
          isMatchRepeatAtSeamsOn,
          isPleatToPatternOn,
          directionOfUseTypeId,
          isSpecialtyFabricHandlingOn,
        } = fabricsOrLiningsItemDetailsFields;

        let detailsPayload: FabricsOrLiningsItemDetailsFieldsPartial = {
          fabricWidthInches,
          verticalRepeatInches,
          horizontalRepeatInches,
          isDropRepeatOn,
          isMatchRepeatAtSeamsOn,
          isPleatToPatternOn,
          directionOfUseTypeId,
          isSpecialtyFabricHandlingOn,
        };

        payload = {
          ...payload,
          ...detailsPayload,
        };
      }
      const pricingArr = [
        { tradePricingMethod: 'tradePricingValue' },
        { retailPricingMethod: 'retailPricingValue' },
        { cost: 'cost' },
        { msrp: 'msrp' },
      ];

      for (const price of pricingArr) {
        for (let [key, value] of Object.entries(price)) {
          if (payload[key] === false || payload[key] === undefined || payload[key] === '') {
            payload[key] = null;
            //   payload[value] = null;
            payload[value as string] = null;
          }
        }
      }

      let result: any;
      try {
        if (itemId) {
          payload = {
            ...payload,
            id: itemId,
            itemId,
          };

          if (isFabricsOrLiningsCategory) {
            result = await updateFabricsOrLiningsRecord(payload);

            if (formFields.categoryName === 'Fabrics & Trims') {
              trackView(MixpanelEvents.SAVE_FABRIC);
            }
          } else {
            result = await updateRecord(payload);
          }
        } else {
          if (isFabricsOrLiningsCategory) {
            result = await addFabricsOrLiningsRecord(payload);

            if (formFields.categoryName === 'Fabrics & Trims') {
              trackView(MixpanelEvents.SAVE_FABRIC);
            }
          } else {
            result = await addRecord(payload);

            if (formFields.categoryName === 'Fees') {
              trackView(MixpanelEvents.SAVE_FEES);

              if (
                formFields &&
                (formFields.cost ||
                  formFields.msrp ||
                  formFields.handlingFeeMethod ||
                  formFields.handlingFeeValue ||
                  formFields.retailPricingMethod ||
                  formFields.retailPricingValue ||
                  formFields.shippingCost ||
                  formFields.tradePricingMethod ||
                  formFields.tradePricingValue)
              ) {
                trackView(MixpanelEvents.SAVE_FEE_PRICING);
              }
            }
          }
        }
      } catch (error) {
        const err = error as any;
        const backendMessage =
          err?.response?.data?.message ||
          err?.response?.data?.msg ||
          err?.message ||
          'There was an error. If the problem persists, please contact support.';

        if (useWideSheet) {
          setSubmitErrorMessage(String(backendMessage));
          return;
        }

        throw new Error(String(backendMessage));
      }

      const errors = result.data.errors;
      const status = result.status;
      if (errors) {
        setAreaScrollInfo({
          scrollX: 0,
          scrollY: 0,
        });
        throw FormValidationError.fromErrorDictionary(errors);
      } else if ([201, 200].includes(status)) {
        if (reloadGrid) await reloadGrid();
        let patternId;

        if (result && result.data === 'Updated') {
          patternId = itemId;
        } else {
          patternId =
            result && result.data && result.data.item && result.data.item.id
              ? result.data.item.id
              : null;
        }

        if (useWideSheet) {
          toast.success(itemId ? 'Component updated successfully' : 'Component added to catalog');
        }

        if (updateFabricsTab)
          await updateFabricsTab({
            ...payload,
            ...result.data.item,
            ...result.data.itemDetails,
            patternId,
          });

        onSaveClose(result.data.item || result?.data);
      }
      if (setSupplierName) setSupplierName('');
      if (handleCloseModal) handleCloseModal();
      if (resetSupplier) resetSupplier();
    } finally {
      if (useWideSheet) {
        setIsSavingWideSheet(false);
      }
    }
  };

  const handleSubmitForm = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    await submitForm();
  };

  const handleKeyPressForm = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' || e.keyCode === 13) {
      e.preventDefault();
    }
  };

  const selectTab = (index: number) => {
    setActiveTabIndex(index);
    setAreaScrollInfo({
      scrollX: 0,
      scrollY: 0,
    });
  };

  const toggleInactive = () => {
    dispatch({
      type: 'updateFormFields',
      formFieldUpdates: { isActive: !formFields.isActive },
    });
  };

  const deepEqual = (obj1: any, obj2: any): boolean => {
    if (obj1 === obj2) return true;

    if (obj1 == null || obj2 == null) return false;

    if (typeof obj1 !== 'object' || typeof obj2 !== 'object') return false;

    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) return false;

    for (let key of keys1) {
      if (!keys2.includes(key) || !deepEqual(obj1[key], obj2[key])) return false;
    }

    return true;
  };

  const showEditWorkOrderConfirm = (): Promise<boolean> => {
    return confirmation(<div>Would you like to cancel your edits?</div>, {
      uiHeader: 'Confirmation',
      uiAcceptLabel: 'No, continue editing',
      uiDeclineLabel: 'Yes, cancel edits',
      workOrderEditMode: true,
      cancelEditing: () => {
        onModalClose();
      },
    });
  };

  const runAfterWideSheetCloseAnimation = (callback: () => void) => {
    if (!useWideSheet) {
      callback();
      return;
    }

    setIsWideSheetVisible(false);

    if (wideSheetCloseTimeoutRef.current !== null) {
      window.clearTimeout(wideSheetCloseTimeoutRef.current);
    }

    wideSheetCloseTimeoutRef.current = window.setTimeout(() => {
      wideSheetCloseTimeoutRef.current = null;
      callback();
    }, WIDE_SHEET_CLOSE_ANIMATION_MS);
  };

  const closeWithoutSaving = (callback?: () => void) => {
    runAfterWideSheetCloseAnimation(() => {
      dispatch({ type: 'resetFormFields' });
      setSheetValidationErrors(initialSheetValidationErrors);
      setSubmitErrorMessage('');
      setIsCloseModalActive(false);
      setActiveTabIndex(0);
      onModalClose();
      if (setSupplierName) setSupplierName('');
      callback?.();
    });
  };

  const showWideSheetEditConfirm = async (onDiscard?: () => void) => {
    setIsCloseModalActive(true);

    const shouldContinueEditing = await confirmation(
      <div>Would you like to cancel your edits?</div>,
      {
        uiHeader: 'Confirmation',
        uiAcceptLabel: 'No, continue editing',
        uiDeclineLabel: 'Yes, cancel edits',
      }
    );

    setIsCloseModalActive(false);

    if (!shouldContinueEditing) {
      closeWithoutSaving(onDiscard);
    }
  };

  const navigateToDescriptionSettings = () => {
    if (typeof window !== 'undefined') {
      window.location.assign('/account_settings?tab=description_settings');
    }
  };

  const handleDescriptionSettingsClick = async () => {
    const redirectToDescriptionSettings = () => {
      navigateToDescriptionSettings();
    };

    if (deepEqual(formFields, copyFormFields)) {
      closeWithoutSaving(redirectToDescriptionSettings);
      return;
    }

    if ((fromCalculator && isUpdateChanged) || !fromCalculator) {
      if (useWideSheet) {
        await showWideSheetEditConfirm(redirectToDescriptionSettings);
        return;
      }

      showEditWorkOrderConfirm().then((shouldContinueEditing) => {
        if (!shouldContinueEditing) {
          closeWithoutSaving(redirectToDescriptionSettings);
        }
      });
      return;
    }

    closeWithoutSaving(redirectToDescriptionSettings);
  };

  const onClose = () => {
    if (!canEditMyShop) {
      closeWithoutSaving();
    } else if (deepEqual(formFields, copyFormFields)) {
      closeWithoutSaving();
    } else if ((fromCalculator && isUpdateChanged) || !fromCalculator) {
      if (useWideSheet) {
        showWideSheetEditConfirm();
        return;
      }

      showEditWorkOrderConfirm();
    } else if (fromCalculator && !isUpdateChanged) {
      closeWithoutSaving();
    }
  };

  const onSaveClose = (item?: any) => {
    runAfterWideSheetCloseAnimation(() => {
      dispatch({ type: 'resetFormFields' });
      setSheetValidationErrors(initialSheetValidationErrors);
      setSubmitErrorMessage('');
      setIsCloseModalActive(false);
      setActiveTabIndex(0);
      onModalClose(item);
      if (setSupplierName) setSupplierName('');
    });
  };

  const handleNextChange = () => {
    if (activeTabIndex < 2) {
      setActiveTabIndex(activeTabIndex! + 1);
    }
  };

  const renderDetails = (): JSX.Element => {
    return (
      <TabContainer>
        <Details
          openModalClick={openModalClick}
          categories={categories}
          suppliers={suppliers}
          descriptionSettings={descriptionSettings}
          isCategory={isCategory}
          isCategoryErrorMessage={isCategoryErrorMessage}
          setIsCategory={setIsCategory}
          setIsCategoryErrorMessage={setIsCategoryErrorMessage}
          isName={isName}
          itemId={itemId}
          setIsName={setIsName}
          browser={browser}
          formFields={formFields}
          fabricsOrLiningsItemDetailsFields={fabricsOrLiningsItemDetailsFields}
          dispatch={dispatch}
          pathIdForModal={pathIdForModal}
          pathNameForModal={pathNameForModal}
          fabricPatternModalCategoryId={fabricPatternModalCategoryId}
          supplierName={supplierName}
          supplierId={supplierId}
          categoryName={categoryNameNew}
          validationError={validationError}
          isNameMessageError={isNameMessageError}
          setIsNameMessageError={setIsNameMessageError}
        />
      </TabContainer>
    );
  };

  const renderPricing = (): JSX.Element => {
    return (
      <TabContainer>
        <Pricing
          highlightedSection={highlightedSection}
          browser={browser}
          formFields={formFields}
          dispatch={dispatch}
          updateStateByName={updateStateByName}
          indexOfMainFabric={indexOfMainFabric}
          clearHighlightSection={clearHighlightSection}
          highLightSection={highLightSection}
          client={client}
          tabIndex={tabIndex}
        />
      </TabContainer>
    );
  };

  const renderSpecifications = (): JSX.Element => {
    return (
      <TabContainer>
        <Specifications
          browser={browser}
          fabricsOrLiningsItemDetailsFields={fabricsOrLiningsItemDetailsFields}
          dispatch={dispatchItemDetails}
          dispatchTwo={dispatch}
          formFields={formFields}
          activeItemClassId={activeItemClassId}
          itemId={itemId}
          highlightedSection={highlightedSection}
        />
      </TabContainer>
    );
  };

  const getModalTitle = (): JSX.Element | '' => {
    const { categoryName, name } = formFields;

    if (!categoryName && !name) {
      return <span className="text-twPrimary font-medium">Add New Item</span>;
    }

    return (
      <div>
        {categoryName && (
          <>
            <span className="text-twBlue">{categoryName}</span>
            <span className="p-1">|</span>
          </>
        )}
        <span className="text-twBlue">{`${name || ''}`}</span>
      </div>
    );
  };

  useEffect(() => {
    if (errorElementId) {
      const errorElement = document.getElementById(errorElementId);
      if (errorElement) {
        errorElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
        errorElement.focus();
      }
    }
  }, [errorElementId]);

  useEffect(() => {
    if (itemId) {
      if (activeItemClassId === ItemClasses.FabricsOrLining) {
        getFabricsOrLiningsRecord({ recordId: itemId })
          .then((res) => {
            const itemUpdates: FormFieldsPartial = {
              ...res,
            };

            const itemDetailsUpdates: FabricsOrLiningsItemDetailsFieldsPartial = {
              ...itemUpdates,
            };
            const supplierName = getSelectedName({
              list: suppliers as [],
              findByKey: 'id',
              findValue: itemUpdates.supplierId,
              nameField: 'displayName',
            });
            const normalizedCategoryFields = normalizeCategoryFields(itemUpdates, categories);

            dispatch({
              type: 'updateFormFields',
              formFieldUpdates: {
                ...itemUpdates,
                ...normalizedCategoryFields,
                supplierName,
              },
            });
            copyDispatch({
              type: 'updateFormFields',
              formFieldUpdates: {
                ...itemUpdates,
                ...normalizedCategoryFields,
                supplierName,
              },
            });
            dispatchItemDetails({
              type: 'updateFormFields',
              formFieldUpdates: {
                ...itemDetailsUpdates,
                fabricWidthInches:
                  itemDetailsUpdates.fabricWidthInches !== undefined
                    ? formatFetchedSpecificationValue(itemDetailsUpdates.fabricWidthInches)
                    : '0',
                verticalRepeatInches:
                  itemDetailsUpdates.verticalRepeatInches !== undefined
                    ? formatFetchedSpecificationValue(itemDetailsUpdates.verticalRepeatInches)
                    : '0',
                horizontalRepeatInches:
                  itemDetailsUpdates.horizontalRepeatInches !== undefined
                    ? formatFetchedSpecificationValue(itemDetailsUpdates.horizontalRepeatInches)
                    : '0',
              },
            });
            const updateObj = {
              ...itemUpdates,
              ...normalizedCategoryFields,
              supplierName,
            };

            if (itemUpdates.fabricsOrLiningsItemDetails) {
              const itemDetailsUpdates: FabricsOrLiningsItemDetailsFieldsPartial = {
                ...itemUpdates.fabricsOrLiningsItemDetails,
              };

              updateObj.fabricsOrLiningsItemDetails = {
                ...(itemDetailsUpdates as FabricsOrLiningsItemDetailsFields),
                fabricWidthInches:
                  formatFetchedSpecificationValue(itemDetailsUpdates.fabricWidthInches, '0') || '0',
                verticalRepeatInches:
                  formatFetchedSpecificationValue(itemDetailsUpdates.verticalRepeatInches, '0') ||
                  '0',
                horizontalRepeatInches:
                  formatFetchedSpecificationValue(itemDetailsUpdates.horizontalRepeatInches, '0') ||
                  '0',
              };
            }

            setFormValues((prev) => ({
              ...prev,
              ...updateObj,
            }));
            setRefetchItemTrigger(true);
          })
          .catch((err) => {
            console.log(`error fetching fabrics or linings item: ${err}`);
          });
      } else {
        getRecord({ recordId: itemId })
          .then((res) => {
            const itemUpdates: FormFieldsPartial = {
              ...res,
            };
            const normalizedCategoryFields = normalizeCategoryFields(itemUpdates, categories);
            let itemDetailsUpdates: FabricsOrLiningsItemDetailsFieldsPartial = {};

            if (itemUpdates.fabricsOrLiningsItemDetails) {
              itemDetailsUpdates = {
                ...itemUpdates.fabricsOrLiningsItemDetails,
              };
              dispatchItemDetails({
                type: 'updateFormFields',
                formFieldUpdates: {
                  ...itemDetailsUpdates,
                  fabricWidthInches: formatFetchedSpecificationValue(
                    itemDetailsUpdates.fabricWidthInches
                  ),
                  verticalRepeatInches: formatFetchedSpecificationValue(
                    itemDetailsUpdates.verticalRepeatInches
                  ),
                  horizontalRepeatInches: formatFetchedSpecificationValue(
                    itemDetailsUpdates.horizontalRepeatInches
                  ),
                },
              });
            }

            const supplierName = getSelectedName({
              list: suppliers as [],
              findByKey: 'id',
              findValue: itemUpdates.supplierId,
              nameField: 'displayName',
            });

            dispatch({
              type: 'updateFormFields',
              formFieldUpdates: {
                ...itemUpdates,
                fabricsOrLiningsItemDetails: {
                  ...(itemDetailsUpdates as FabricsOrLiningsItemDetailsFields),
                  fabricWidthInches:
                    formatFetchedSpecificationValue(itemDetailsUpdates.fabricWidthInches, '0') ||
                    '0',
                  verticalRepeatInches:
                    formatFetchedSpecificationValue(itemDetailsUpdates.verticalRepeatInches, '0') ||
                    '0',
                  horizontalRepeatInches:
                    formatFetchedSpecificationValue(
                      itemDetailsUpdates.horizontalRepeatInches,
                      '0'
                    ) || '0',
                },
                ...normalizedCategoryFields,
                supplierName,
              },
            });
            copyDispatch({
              type: 'updateFormFields',
              formFieldUpdates: {
                ...itemUpdates,
                fabricsOrLiningsItemDetails: {
                  ...(itemDetailsUpdates as FabricsOrLiningsItemDetailsFields),
                  fabricWidthInches:
                    formatFetchedSpecificationValue(itemDetailsUpdates.fabricWidthInches, '0') ||
                    '0',
                  verticalRepeatInches:
                    formatFetchedSpecificationValue(itemDetailsUpdates.verticalRepeatInches, '0') ||
                    '0',
                  horizontalRepeatInches:
                    formatFetchedSpecificationValue(
                      itemDetailsUpdates.horizontalRepeatInches,
                      '0'
                    ) || '0',
                },
                ...normalizedCategoryFields,
                supplierName,
              },
            });
            const updateObj = {
              ...itemUpdates,
              ...normalizedCategoryFields,
              supplierName,
            };
            if (itemUpdates.fabricsOrLiningsItemDetails) {
              const itemDetailsUpdates: FabricsOrLiningsItemDetailsFieldsPartial = {
                ...itemUpdates.fabricsOrLiningsItemDetails,
              };
              updateObj.fabricsOrLiningsItemDetails = {
                ...(itemDetailsUpdates as FabricsOrLiningsItemDetailsFields),
                fabricWidthInches:
                  formatFetchedSpecificationValue(itemDetailsUpdates.fabricWidthInches, '0') || '0',
                verticalRepeatInches:
                  formatFetchedSpecificationValue(itemDetailsUpdates.verticalRepeatInches, '0') ||
                  '0',
                horizontalRepeatInches:
                  formatFetchedSpecificationValue(itemDetailsUpdates.horizontalRepeatInches, '0') ||
                  '0',
              };
            }
            if (itemUpdates.fabricsOrLiningsItemDetails) {
              setFormValues((prev) => ({
                ...prev,
                ...updateObj,
              }));
            }
            setRefetchItemTrigger(true);
          })
          .catch((err) => {
            console.log(`error fetching drapery item: ${err}`);
          });
      }
    } else {
      setRefetchItemTrigger(true);
    }
  }, [itemId, activeItemClassId]);

  useEffect(() => {
    if (itemId || !fabricPatternModalCategoryId || hasAppliedDefaultFabricPatternCategory.current) {
      return;
    }

    dispatch({
      type: 'updateFormFields',
      formFieldUpdates: getDefaultCategoryFields(fabricPatternModalCategoryId, categories),
    });
    hasAppliedDefaultFabricPatternCategory.current = true;
  }, [categories, fabricPatternModalCategoryId, itemId]);
  useEffect(() => {
    if (useWideSheet) {
      return;
    }

    const html = document.querySelector('html')!;
    if (isModalOpen) {
      html.style.position = 'fixed';
      html.style.width = '100%';
    } else {
      html.style.position = 'static';
      html.style.width = 'initial';
    }
  }, [isModalOpen, useWideSheet]);
  useEffect(() => {
    setCategoryName(formFields.categoryName);
  }, [formFields.categoryName]);
  useEffect(() => {
    if (isModalOpen) {
      setSheetValidationErrors(initialSheetValidationErrors);
    }
  }, [isModalOpen]);

  // MIXPANEL TRACKING ***
  useEffect(() => {
    if (refetchItemTrigger) {
      const index_view_relation: any = {};

      const lower_chosen_category_name =
        formFields && formFields.categoryName ? formFields.categoryName.toLowerCase() : '';
      const views: any = {
        fabrication: [
          MixpanelEvents.MY_SHOP_FABRICATION_DETAILS,
          MixpanelEvents.MY_SHOP_FABRICATION_PRICING,
        ],
        'fabrics & trims': [
          MixpanelEvents.MY_SHOP_FABRICS_DETAILS,
          MixpanelEvents.MY_SHOP_FABRICS_PRICING,
          MixpanelEvents.MY_SHOP_FABRICS_SPECIFICATIONS,
        ],
        fees: [MixpanelEvents.MY_SHOP_FEES_DETAILS, MixpanelEvents.MY_SHOP_FEES_PRICING],
        linings: [
          MixpanelEvents.MY_SHOP_LININGS_DETAILS,
          MixpanelEvents.MY_SHOP_LININGS_PRICING,
          MixpanelEvents.MY_SHOP_LININGS_SPECIFICATIONS,
        ],
        add_new_item: [
          MixpanelEvents.MY_SHOP_ADD_ITEM_DETAILS,
          MixpanelEvents.MY_SHOP_ADD_ITEM_PRICING,
        ],
      };

      const selected_views =
        !lower_chosen_category_name || !formFields.categoryId || !views[lower_chosen_category_name]
          ? views['add_new_item']
          : views[lower_chosen_category_name];

      for (let i = 0; i < selected_views.length; i++) {
        index_view_relation[i] = selected_views[i];
      }
      trackView(index_view_relation[activeTabIndex], {});
    }
  }, [activeTabIndex, refetchItemTrigger, formFields.categoryId]);

  useEffect(() => {
    if (isModalOpen && refetchItemTrigger) {
      const index_view_relation: any = {};

      const lower_chosen_category_name =
        formFields && formFields.categoryName ? formFields.categoryName.toLowerCase() : '';
      const view_pop_ups: any = {
        fabrication: [MixpanelEvents.MY_SHOP_FABRICATION],
        'fabrics & trims': [MixpanelEvents.MY_SHOP_FABRICS],
        fees: [MixpanelEvents.MY_SHOP_FEES],
        linings: [MixpanelEvents.MY_SHOP_LININGS],
        add_new_item: [MixpanelEvents.MY_SHOP_ADD_ITEM],
      };
      const selected_views =
        !lower_chosen_category_name ||
        !formFields.categoryId ||
        !view_pop_ups[lower_chosen_category_name]
          ? view_pop_ups['add_new_item']
          : view_pop_ups[lower_chosen_category_name];

      index_view_relation[0] = selected_views[0];
      trackView(index_view_relation[0], {});
    }
  }, [itemId, refetchItemTrigger]);

  /** END OF MIXPANEL TRACKING *** */

  const isSheetHiddenForOverlayModal = isSupplierModalOpen;
  const wideSheetStyle = isCloseModalActive
    ? { display: 'none' }
    : isSheetHiddenForOverlayModal
      ? { zIndex: 999, visibility: 'hidden' }
      : undefined;
  const wideSheetOverlayStyle =
    isSupplierModalOpen || isCloseModalActive
      ? { display: 'none' }
      : { backgroundColor: 'rgba(0, 0, 0, 0.78)' };

  let selectedCategory = categories?.find((f: any) => f.id === formFields?.categoryId);
  let noSupplierRequired = formFields?.categoryId === 102 || selectedCategory?.name?.toLowerCase() === 'fees';

  if (useWideSheet) {
    return (
      <>
        <Prompt
          message={(location: any): string | boolean => {
            if (isModalOpen) {
              if (useWideSheet) {
                showWideSheetEditConfirm();
              } else {
                showEditWorkOrderConfirm();
              }
              return false;
            } else if (!fromCalculator) {
              window.location.replace('/myshop');
              return true;
            }
            return true;
          }}
        />
        <WideSheet
          canSave={canEditMyShop}
          open={isModalOpen && isWideSheetVisible}
          onCancel={onClose}
          onSave={() => {
            void submitForm();
          }}
          sheetStyle={wideSheetStyle}
          title={itemId ? 'Edit My Shop Item' : 'Add to My Shop'}
          subTitle="Manage component and fee details for your shop"
          saveLabel={isSavingWideSheet ? 'Saving...' : 'Save'}
          cancelLabel="Cancel"
          saveLoading={isSavingWideSheet}
          overlayStyle={wideSheetOverlayStyle}
        >
          <form
            id="my-shop-item-sheet-form"
            onSubmit={handleSubmitForm}
            onKeyPress={handleKeyPressForm}
            className="space-y-6"
          >
            <MyShopItemWideSheetContent
              categories={categories}
              suppliers={suppliers}
              descriptionSettings={descriptionSettings}
              formFields={formFields}
              fabricsOrLiningsItemDetailsFields={fabricsOrLiningsItemDetailsFields}
              dispatch={dispatch}
              dispatchItemDetails={dispatchItemDetails}
              validationErrors={sheetValidationErrors}
              setValidationErrors={setSheetValidationErrors}
              isFabricsOrLiningsCategory={isFabricsOrLiningsCategory}
              isEditMode={!!itemId}
              readOnlyPricing={readOnlyPricing}
              hideSubtype={hideSubtype}
              lockCategory={!!categoryId && !itemId}
              submitErrorMessage={submitErrorMessage}
              onOpenSupplierModal={openModalClick}
              onDescriptionSettingsClick={handleDescriptionSettingsClick}
              canEditMyShop={canEditMyShop}
              prefill={prefill}
              lockPrefill={lockPrefill}
              noSupplierRequired={noSupplierRequired}
            />
          </form>
        </WideSheet>
      </>
    );
  }

  return (
    <>
      <Prompt
        message={(location: any): string | boolean => {
          if (isModalOpen) {
            showEditWorkOrderConfirm();
            return false;
          } else {
            window.location.replace('/myshop');
            return true;
          }
        }}
      />
      <PageDimmer uiActive={isModalOpen} />
      <ModalItem
        uiActive={isModalOpen}
        uiOnRequestClose={() => {
          return;
        }}
        uiDefaultSizes={false}
      >
        <ValidationFormItem
          id="validation-form"
          uiOnSubmit={handleSubmitForm}
          uiSubmitFailedMessage=""
          uiReportValidityMode="firstInvalid"
          uiOnInvalidElem={handleInvalidElem}
          onKeyPress={handleKeyPressForm}
          uiPadding={false}
          className="h-full"
        >
          <ModalHeader className="border-0">
            <div className="flex justify-between w-full pb-2 border-b-8 border-twSecondary">
              {getModalTitle()}
              <Checkbox
                checked={!formFields.isActive}
                onClick={toggleInactive}
                className={`font-normal text-base ${inactiveItemError && !formFields.isActive ? 'uiError' : ''}`}
              >
                Make Inactive
              </Checkbox>
            </div>
          </ModalHeader>

          <ItemModalContent className="mb-8">
            <PopoverScrollContext.Provider value={areaScrollInfo}>
              <ItemTabs
                selectedIndex={activeTabIndex}
                onSelect={selectTab}
                forceRenderTabPanel
                className="flex flex-col flex-1 h-full"
              >
                <TabList>
                  <Tab>Details</Tab>
                  <Tab>Pricing</Tab>
                  {isFabricsOrLiningsCategory && <Tab>Specifications</Tab>}
                </TabList>
                {renderDetails()}
                {renderPricing()}
                {isFabricsOrLiningsCategory && renderSpecifications()}
              </ItemTabs>
            </PopoverScrollContext.Provider>
          </ItemModalContent>
          {((activeTabIndex > 1 && isFabricsOrLiningsCategory) ||
            (activeTabIndex > 0 && !isFabricsOrLiningsCategory)) && (
            <ModalActions>
              <CancelButton
                type="button"
                uiColor="secondary"
                onClick={onClose}
                className="cancel_btn rounded-lg mr-auto block shadow-none"
              >
                Cancel
              </CancelButton>

              <Button
                className="primary_btn rounded-lg border border-twBlue2 block shadow-none"
                uiColor="primary"
              >
                Save
              </Button>
            </ModalActions>
          )}
          {((activeTabIndex < 2 && isFabricsOrLiningsCategory) ||
            (activeTabIndex === 0 && !isFabricsOrLiningsCategory)) && (
            <ModalActions>
              <CancelButton
                type="button"
                uiColor="secondary"
                onClick={onClose}
                className="cancel_btn rounded-lg mr-auto block shadow-none"
              >
                Cancel
              </CancelButton>

              <CancelButton
                type="button"
                className="cancel_btn rounded-lg block shadow-none"
                uiColor="secondary"
                onClick={handleNextChange}
              >
                Next
              </CancelButton>
            </ModalActions>
          )}
        </ValidationFormItem>
        {/* <ConfirmationCloseModal
                    uiAcceptLabel="Yes"
                    uiDeclineLabel="No"
                    uiHeader="Confirmation"
                    uiActive={isCloseModalActive}
                    uiOnConfirm={() => {
                        dispatch({ type: "resetFormFields" });
                        setActiveTabIndex(0);
                        onModalClose();
                        if (setSupplierName) setSupplierName("");
                    }}
                    uiOnClose={() => setIsCloseModalActive(false)}
                    uiOnRequestClose={() => setIsCloseModalActive(false)}
                    uiCloseOnOutsideClick={true}
                    uiCloseOnEsc={true}
                >
                    Are you sure you want to exit without saving the changes?
                </ConfirmationCloseModal> */}
      </ModalItem>
    </>
  );
};

export default StandardItemModal;
